// Scout's First Look — the instant time-to-wow moment for a brand-new team.
//
// A coach who drops a GameChanger CSV in the onboarding wizard hands us a full
// season of real numbers at minute one. This module turns those numbers into
// 2–3 insight cards computed entirely client-side (no AI call, no games played
// in Dugout yet) so something impressive greets the coach the moment they land
// on Home.
//
// Reads from the player docs the wizard wrote: `player.stats` (current-season
// mode) or `player.priorSeasonStats.stats` (prior-baseline mode). Once real
// per-game data exists in Dugout, the Home tab stops showing these — the live
// dashboard takes over.
//
// Voice rules (bench coach): every card is a positive standout, suggestive and
// never corrective. One card per player, so the wow spreads across the roster.

export interface FirstLookCard {
  key: string;
  playerName: string;   // first name only — reads like dugout chatter
  tag: string;          // short label, e.g. "On-Base Machine"
  headline: string;     // the bench-coach sentence
  statValue: string;    // the big number, e.g. ".482"
  statLabel: string;    // e.g. "OBP"
}

export interface FirstLook {
  cards: FirstLookCard[];
  source: 'current' | 'prior';  // which stat field the numbers came from
}

const MIN_PA = 10;  // small-sample guard for batting cards
const MIN_IP = 4;   // and for pitching

const firstName = (name: string) =>
  (name || '').trim().split(/\s+/)[0] || 'This player';

const fmtRate = (v: number) => v.toFixed(3).replace(/^0/, '');

export function buildFirstLook(players: any[]): FirstLook | null {
  if (!players || players.length === 0) return null;

  // The wizard routes the whole roster's stats to one destination, so detect
  // the source once: live `stats` wins if any player has usable numbers there.
  const usable = (s: any) => s && ((s.pa ?? 0) > 0 || (s.ip ?? 0) > 0);
  const hasCurrent = players.some(p => usable(p.stats));
  const hasPrior = players.some(p => usable(p.priorSeasonStats?.stats));
  if (!hasCurrent && !hasPrior) return null;
  const source: 'current' | 'prior' = hasCurrent ? 'current' : 'prior';

  const lines = players
    .map(p => ({ p, s: source === 'current' ? p.stats : p.priorSeasonStats?.stats }))
    .filter(({ s }) => usable(s));
  if (lines.length === 0) return null;

  const batters = lines.filter(({ s }) => (s.pa ?? 0) >= MIN_PA);
  const candidates: Array<FirstLookCard & { playerId: string }> = [];

  // On-base leader — the leadoff conversation.
  const onBase = batters
    .filter(({ s }) => (s.obp ?? 0) >= 0.35)
    .sort((a, b) => (b.s.obp ?? 0) - (a.s.obp ?? 0))[0];
  if (onBase) {
    const pct = Math.round((onBase.s.obp ?? 0) * 100);
    candidates.push({
      key: 'onbase', playerId: onBase.p.id,
      playerName: firstName(onBase.p.name),
      tag: 'On-Base Machine',
      headline: `Reaches base in ${pct}% of trips to the plate. That's a leadoff conversation.`,
      statValue: fmtRate(onBase.s.obp ?? 0),
      statLabel: 'OBP',
    });
  }

  // Strikeout arm — most K's with a real workload.
  const arm = lines
    .filter(({ s }) => (s.ip ?? 0) >= MIN_IP && (s.soAllowed ?? 0) >= 5)
    .sort((a, b) => (b.s.soAllowed ?? 0) - (a.s.soAllowed ?? 0))[0];
  if (arm) {
    candidates.push({
      key: 'arm', playerId: arm.p.id,
      playerName: firstName(arm.p.name),
      tag: 'Arm to Watch',
      headline: `${arm.s.soAllowed} strikeouts across ${arm.s.ip} innings. An arm worth planning your week around.`,
      statValue: String(arm.s.soAllowed),
      statLabel: 'K',
    });
  }

  // Biggest bat — OPS leader.
  const bigBat = batters
    .filter(({ s }) => (s.ops ?? 0) >= 0.65)
    .sort((a, b) => (b.s.ops ?? 0) - (a.s.ops ?? 0))[0];
  if (bigBat) {
    candidates.push({
      key: 'bigbat', playerId: bigBat.p.id,
      playerName: firstName(bigBat.p.name),
      tag: 'The Big Bat',
      headline: `Leads the roster in OPS. When an inning needs a spark, this bat has been the answer.`,
      statValue: fmtRate(bigBat.s.ops ?? 0),
      statLabel: 'OPS',
    });
  }

  // Wheels — stolen-base threat.
  const wheels = batters
    .filter(({ s }) => (s.sb ?? 0) >= 4)
    .sort((a, b) => (b.s.sb ?? 0) - (a.s.sb ?? 0))[0];
  if (wheels) {
    candidates.push({
      key: 'wheels', playerId: wheels.p.id,
      playerName: firstName(wheels.p.name),
      tag: 'Wheels',
      headline: `${wheels.s.sb} stolen bases. Once this player reaches first, the next base is already in play.`,
      statValue: String(wheels.s.sb),
      statLabel: 'SB',
    });
  }

  // Contact — puts the ball in play, rarely strikes out.
  const contact = batters
    .filter(({ s }) => (s.pa ?? 0) > 0 && (s.so ?? 0) / s.pa <= 0.15)
    .sort((a, b) => (a.s.so ?? 0) / a.s.pa - (b.s.so ?? 0) / b.s.pa)[0];
  if (contact) {
    const kPct = Math.round(((contact.s.so ?? 0) / contact.s.pa) * 100);
    candidates.push({
      key: 'contact', playerId: contact.p.id,
      playerName: firstName(contact.p.name),
      tag: 'Contact Machine',
      headline: `Strikes out in just ${kPct}% of plate appearances. The ball goes where the defense isn't.`,
      statValue: `${kPct}%`,
      statLabel: 'K Rate',
    });
  }

  // Batter's eye — walks more than strikes out.
  const eye = batters
    .filter(({ s }) => (s.bb ?? 0) >= 5 && (s.bb ?? 0) > (s.so ?? 0))
    .sort((a, b) => (b.s.bb ?? 0) - (a.s.bb ?? 0))[0];
  if (eye) {
    candidates.push({
      key: 'eye', playerId: eye.p.id,
      playerName: firstName(eye.p.name),
      tag: 'Sees It Well',
      headline: `More walks than strikeouts. A batter's eye you can build innings around.`,
      statValue: String(eye.s.bb),
      statLabel: 'BB',
    });
  }

  // One card per player, first three in priority order above.
  const seen = new Set<string>();
  const cards: FirstLookCard[] = [];
  for (const c of candidates) {
    if (seen.has(c.playerId)) continue;
    seen.add(c.playerId);
    const { playerId, ...card } = c;
    cards.push(card);
    if (cards.length === 3) break;
  }

  return cards.length > 0 ? { cards, source } : null;
}
